import { getDefaultAvatar } from "@repo/fika-design-system/avatar"
import { parseLocale } from "@repo/fika-shared/src/locale"
import { translate } from "@repo/fika-shared/src/translate"
import { useNavigate } from "@solidjs/router"
import { createQuery } from "@urql/solid"
import { format } from "date-fns"
import { createMemo, createSignal, For, Show } from "solid-js"
import { urqlClient } from "@/context/graphql-provider"
import { http } from "@/data/http"
import { Box } from "@/designSystem/components/box"
import { Button } from "@/designSystem/components/button"
import {
  PageInfoFragment,
  Pagination,
} from "@/designSystem/components/pagination"
import { Table } from "@/designSystem/components/table"
import { MainLayout } from "@/designSystem/layouts/main-layout"
import { Info } from "@/designSystem/patterns/info"
import { NavigationWithButtons } from "@/designSystem/patterns/navigation"
import { PublicationTabs } from "@/designSystem/patterns/publication-tabs"
import SearchForm from "@/designSystem/patterns/search-form"
import { graphql } from "@/graphql"
import { useI18nContext } from "@/i18n/i18n-solid"
import {
  createMutationWithErrorHandling,
  ErrorFragment,
} from "@/lib/graphql-mutation-helpers"
import { useRelativeTime } from "@/lib/use-relative-time"
import {
  createBlogRouter,
  SUBSCRIBER_LIST_DEFAULTS,
  type SubscriberListParams,
} from "@/routes/posts-router"
import {
  publicationCellClass,
  publicationLinkClass,
  subscriberAvatarClass,
  subscriberEmailCellClass,
  truncatedCellClass,
} from "./publication-subscribers.css"

const SPECIAL_TIER_FILTERS = ["all", "free"] as const
type SpecialTierFilter = (typeof SPECIAL_TIER_FILTERS)[number]

function isSpecialTierFilter(value: string): value is SpecialTierFilter {
  return SPECIAL_TIER_FILTERS.includes(value as SpecialTierFilter)
}

const SubscribersQuery = graphql(
  `query Subscribers($tierId: String, $formId: String, $first: Int, $after: String, $before: String, $last: Int) {
    me {
      id
      publication {
        id
        leadMagnetForms {
          id
          name
        }
        subscriptionTiers {
          id
          name
        }
        subscribers( tierId: $tierId, formId: $formId, first: $first, after: $after, before: $before, last: $last) {
          edges {
            cursor
            node {
              id
              email
              subscribedAt
              formName
              tier {
                id
                name
              }
              searchResult {
                rank
                highlight {
                  email
                }
              }
              user {
                id
                profileImage
                publication {
                  id
                  title
                  url
                  logoUrl
                  sub { id }
                }
              }
            }
          }
          pageInfo { ...PageInfoFragment }
        }
      }
    }
  }`,
  [PageInfoFragment],
)

const FollowBackMutation = graphql(
  `mutation FollowBack($targetUserId: String!) {
    subscribeTo(targetUserId: $targetUserId) {
      __typename
      ... on MutationSubscribeToSuccess { data { id } }
      ...ErrorFragment
    }
  }`,
  [ErrorFragment],
)

function getVariables(params: SubscriberListParams) {
  return {
    tierId: params.tierId,
    formId: params.formId,
    ...(params.before
      ? { before: params.before, last: 20 }
      : { after: params.after || undefined, first: 20 }),
  }
}

export function preloadPublicationSubscribers() {
  return urqlClient
    .query(SubscribersQuery, getVariables(SUBSCRIBER_LIST_DEFAULTS))
    .toPromise()
}

export default function PublicationSubscribers() {
  const { LL, locale } = useI18nContext()
  const interfaceLocale = createMemo(() => parseLocale(locale()))
  const { paths, subscriberListParams, listParams } = createBlogRouter()
  const navigate = useNavigate()
  const variables = createMemo(() => getVariables(subscriberListParams()))

  const [subscribersResult] = createQuery({
    query: SubscribersQuery,
    variables,
  })

  const publication = createMemo(() => subscribersResult.data?.me?.publication)
  const subscribersMetadata = createMemo(() => publication()?.subscribers)
  const subscribers = createMemo(() =>
    (subscribersMetadata()?.edges ?? []).map(edge => ({
      id: edge.node.id,
      email: edge.node.email,
      subscribedAt: edge.node.subscribedAt,
      formName: edge.node.formName
        ? (translate(edge.node.formName, interfaceLocale()) ??
          edge.node.formName)
        : LL().blog.subscribersPage.direct(),
      tierName: edge.node.tier
        ? (translate(edge.node.tier.name, interfaceLocale()) ??
          edge.node.tier.name)
        : null,
      userId: edge.node.user.id,
      profileImage: edge.node.user.profileImage,
      publication: edge.node.user.publication
        ? {
            id: edge.node.user.publication.id,
            title:
              translate(edge.node.user.publication.title, interfaceLocale()) ??
              edge.node.user.publication.title,
            url: edge.node.user.publication.url,
            logoUrl: edge.node.user.publication.logoUrl,
            isFollowing: Boolean(edge.node.user.publication.sub),
          }
        : null,
    })),
  )
  const pageInfo = createMemo(() => subscribersMetadata()?.pageInfo)
  const hasTiers = createMemo(
    () => (publication()?.subscriptionTiers?.length ?? 0) > 0,
  )

  const tierOptions = createMemo(() => {
    const tiers = publication()?.subscriptionTiers ?? []
    const filterLabels: Record<SpecialTierFilter, string> = {
      all: LL().blog.subscribersPage.allTiers(),
      free: LL().blog.subscribersPage.free(),
    }
    const specialFilters = SPECIAL_TIER_FILTERS.map(filter => ({
      value: filter,
      label: filterLabels[filter],
    }))

    return {
      values: [
        ...specialFilters,
        ...tiers.map(tier => ({
          value: tier.id,
          label: translate(tier.name, interfaceLocale()) ?? tier.name,
        })),
      ],
      value: subscriberListParams().tierId ?? "all",
    }
  })

  const [_followBackResult, followBackMutation] =
    createMutationWithErrorHandling(FollowBackMutation, "subscribeTo")

  async function handleFollowBack(targetUserId: string) {
    const result = await followBackMutation({ targetUserId })
    if (result.success) {
      urqlClient
        .query(SubscribersQuery, variables(), {
          requestPolicy: "network-only",
        })
        .toPromise()
    }
  }

  const [isExporting, setIsExporting] = createSignal(false)
  const formOptions = createMemo(() => {
    const forms = publication()?.leadMagnetForms ?? []
    return {
      values: [
        { value: "all", label: LL().blog.subscribersPage.allForms() },
        { value: "direct", label: LL().blog.subscribersPage.direct() },
        ...forms.map(form => ({
          value: form.id,
          label: translate(form.name, interfaceLocale()) ?? form.name,
        })),
      ],
      value: subscriberListParams().formId ?? "all",
    }
  })

  async function handleExport() {
    setIsExporting(true)
    try {
      const formId = subscriberListParams().formId
      const queryString = formId ? `?formId=${encodeURIComponent(formId)}` : ""
      const response = await http(`/api/exports/subscribers${queryString}`)
      if (!response.ok) {
        throw new Error("Failed to export subscribers")
      }
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = "subscribers.csv"
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <MainLayout
      hue="amber"
      header={
        <NavigationWithButtons hue="amber" title={LL().blog.title()}>
          <Button
            hue="amber"
            iconName="download-cloud"
            onClick={handleExport}
            active={isExporting()}
          >
            {LL().blog.export.download()}
          </Button>
        </NavigationWithButtons>
      }
    >
      <Box gap="16">
        <PublicationTabs />
        <SearchForm
          onChange={({ query, tag, filter }) => {
            navigate(
              paths(listParams()).audience({
                query,
                tierId:
                  tag && isSpecialTierFilter(tag) && tag === "all"
                    ? undefined
                    : tag,
                formId: filter === "all" ? undefined : filter,
                after: "",
                before: "",
              }),
            )
          }}
          filter={formOptions()}
          tag={hasTiers() ? tierOptions() : undefined}
        />

        <Show
          when={!subscribersResult.fetching}
          fallback={<Info>{LL().common.loading()}</Info>}
        >
          <Show
            when={subscribers().length > 0}
            fallback={
              <Info>
                {subscriberListParams().query.length
                  ? LL().blog.subscribersPage.noSubscribersFound()
                  : LL().blog.subscribersPage.noSubscribersYet()}
              </Info>
            }
          >
            <Table>
              <Show
                when={hasTiers()}
                fallback={
                  <colgroup>
                    <col style={{ width: "28%" }} />
                    <col style={{ width: "15%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "47%" }} />
                  </colgroup>
                }
              >
                <colgroup>
                  <col style={{ width: "25%" }} />
                  <col style={{ width: "14%" }} />
                  <col style={{ width: "8%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "43%" }} />
                </colgroup>
              </Show>
              <Table.Head>
                <Table.Header>{LL().blog.subscribersPage.email()}</Table.Header>
                <Table.Header>
                  {LL().blog.subscribersPage.subscribedAt()}
                </Table.Header>
                <Table.Header>{LL().blog.subscribersPage.form()}</Table.Header>
                <Show when={hasTiers()}>
                  <Table.Header>
                    {LL().blog.subscribersPage.tier()}
                  </Table.Header>
                </Show>
                <Table.Header>
                  {LL().blog.subscribersPage.publication()}
                </Table.Header>
              </Table.Head>
              <Table.Body>
                <For each={subscribers()}>
                  {subscriber => (
                    <Table.Row>
                      <Table.Cell>
                        <div class={subscriberEmailCellClass}>
                          <img
                            src={
                              subscriber.profileImage ??
                              getDefaultAvatar(subscriber.userId)
                            }
                            alt=""
                            class={subscriberAvatarClass}
                          />
                          <span class={truncatedCellClass}>
                            {subscriber.email}
                          </span>
                        </div>
                      </Table.Cell>
                      <Table.Cell>
                        <TimeRow date={subscriber.subscribedAt} />
                      </Table.Cell>
                      <Table.Cell>{subscriber.formName}</Table.Cell>
                      <Show when={hasTiers()}>
                        <Table.Cell>
                          <span class={truncatedCellClass}>
                            {subscriber.tierName ??
                              LL().blog.subscribersPage.free()}
                          </span>
                        </Table.Cell>
                      </Show>
                      <Table.Cell>
                        <Show when={subscriber.publication} fallback="-">
                          {pub => (
                            <div class={publicationCellClass}>
                              <img
                                src={
                                  pub().logoUrl ?? getDefaultAvatar(pub().id)
                                }
                                alt=""
                                class={subscriberAvatarClass}
                              />
                              <a
                                href={pub().url}
                                class={publicationLinkClass}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {pub().title}
                              </a>
                              <Show
                                when={!pub().isFollowing}
                                fallback={
                                  <Button
                                    hue="amber"
                                    kind="surface"
                                    size="s"
                                    disabled
                                  >
                                    {LL().blog.subscribersPage.following()}
                                  </Button>
                                }
                              >
                                <Button
                                  hue="amber"
                                  kind="surface"
                                  size="s"
                                  onClick={() => handleFollowBack(pub().id)}
                                >
                                  {LL().blog.subscribersPage.followBack()}
                                </Button>
                              </Show>
                            </div>
                          )}
                        </Show>
                      </Table.Cell>
                    </Table.Row>
                  )}
                </For>
              </Table.Body>
            </Table>
          </Show>
        </Show>

        <Pagination
          pageInfo={pageInfo()}
          buildUrl={params => paths(listParams()).audience(params)}
        />
      </Box>
    </MainLayout>
  )
}

function TimeRow(props: { date: Date }) {
  const relativeTime = useRelativeTime(() => props.date)
  const formattedDate = createMemo(() => format(props.date, "PPp"))

  return (
    <time title={formattedDate()} datetime={formattedDate()}>
      {relativeTime()}
    </time>
  )
}
